import React, { useState, useEffect } from 'react';
import { API } from '../App';
import axios from 'axios';
import { TrendingUp, DollarSign, PieChart, Loader2, Briefcase } from 'lucide-react';

const fmt = (n) => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(n || 0);

const portfolioLabels = { bajo: 'Riesgo Bajo', medio: 'Riesgo Medio', alto: 'Riesgo Alto' };
const portfolioColors = { bajo: 'text-emerald-400', medio: 'text-[#D4AF37]', alto: 'text-red-400' };

const InvestorDashboard = ({ user }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/investor/my-portfolio`, { withCredentials: true })
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="min-h-screen bg-[#0f1724] flex items-center justify-center"><Loader2 className="animate-spin text-[#D4AF37]" size={40} /></div>;

  const socio = data?.socio;
  const investments = data?.investments || [];
  const pType = data?.portfolio_type || 'medio';

  return (
    <div className="min-h-screen bg-[#0f1724] p-4 md:p-8" data-testid="investor-dashboard">
      <div className="max-w-5xl mx-auto">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
            Mi Portafolio de Inversión
          </h1>
          <p className="text-gray-400 text-sm">{user?.name} — {socio?.nombre_completo || user?.email}</p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-8">
          <div className="bg-[#1a2332] border border-[#2a3444] rounded-xl p-5">
            <DollarSign className="text-[#D4AF37] mb-2" size={24} />
            <div className="text-2xl font-bold text-white">{fmt(data?.total_invested)}</div>
            <div className="text-sm text-gray-400">Total Invertido</div>
          </div>
          <div className="bg-[#1a2332] border border-[#2a3444] rounded-xl p-5">
            <TrendingUp className="text-emerald-400 mb-2" size={24} />
            <div className="text-2xl font-bold text-emerald-400">{fmt(data?.expected_monthly)}</div>
            <div className="text-sm text-gray-400">Esperado Fin de Mes</div>
          </div>
          <div className="bg-[#1a2332] border border-[#2a3444] rounded-xl p-5">
            <PieChart className="text-blue-400 mb-2" size={24} />
            <div className="text-2xl font-bold text-white">{socio?.porcentaje_participacion || 0}%</div>
            <div className="text-sm text-gray-400">Participación</div>
          </div>
          <div className="bg-[#1a2332] border border-[#2a3444] rounded-xl p-5">
            <Briefcase className={`${portfolioColors[pType]} mb-2`} size={24} />
            <div className={`text-lg font-bold ${portfolioColors[pType]}`}>{portfolioLabels[pType]}</div>
            <div className="text-sm text-gray-400">Tipo de Portafolio</div>
          </div>
        </div>

        {/* Investments Table */}
        <div className="bg-[#1a2332] border border-[#2a3444] rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-[#2a3444]">
            <h2 className="text-white font-bold">Mis Inversiones</h2>
          </div>
          {investments.length === 0 ? (
            <div className="p-12 text-center text-gray-500">No tienes inversiones activas.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#141b2d]">
                  <tr>
                    {['Préstamo', 'Deudor', 'Aporte', '% Participación', '% Interés', 'Interés Mensual', 'Estado'].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-gray-500 font-medium">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {investments.map((inv, i) => (
                    <tr key={i} className="border-t border-[#2a3444]/50 hover:bg-[#141b2d]/50">
                      <td className="px-4 py-3 font-mono text-[#D4AF37]">{inv.consecutivo}</td>
                      <td className="px-4 py-3 text-white">{inv.deudor_nombre}</td>
                      <td className="px-4 py-3 text-white">{fmt(inv.aporte)}</td>
                      <td className="px-4 py-3 text-gray-400">{inv.porcentaje?.toFixed(1)}%</td>
                      <td className="px-4 py-3 text-gray-400">{inv.porcentaje_interes}%</td>
                      <td className="px-4 py-3 text-emerald-400 font-medium">{fmt(inv.monthly_interest)}</td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-0.5 rounded-full text-xs bg-emerald-500/20 text-emerald-400">{inv.estado_prestamo}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-[#141b2d]">
                  <tr>
                    <td colSpan={2} className="px-4 py-3 text-white font-bold">Total</td>
                    <td className="px-4 py-3 text-white font-bold">{fmt(data?.total_invested)}</td>
                    <td colSpan={2}></td>
                    <td className="px-4 py-3 text-emerald-400 font-bold">{fmt(data?.expected_monthly)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InvestorDashboard;
